import { CSSProperties, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCollectionContext } from 'components/settings/collectionContext';
import { SECTION_COLLECTION_NAME } from 'models';
import { arrayMove } from './interfaces';
import { itemStyles, Segments } from './segment';
import { useContractContext } from './contractContext';
import { useTranslationsMap } from 'components/contractLanguagePage/contractLanguagePage';

import './contract.css';
import { useLocation } from 'react-router-dom';
import useEnhancedEffect from '@mui/material/utils/useEnhancedEffect';

const getSectionStyle = (isDragging: boolean, draggableStyle: CSSProperties): CSSProperties => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    // change background colour if dragging
    background: isDragging ? 'lightgreen' : 'inherit',
    // styles we need to apply on draggables
    ...draggableStyle,
});
const getSectionListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? 'lightblue' : 'inherit',
});

export const ContractSidePanel = ({
    translated,
    showLabel,
}: {
    translated?: boolean;
    showLabel?: boolean;
}) => {
    const [, loading, , sectionMap] = useCollectionContext(SECTION_COLLECTION_NAME);
    const { selection, setSelection, contract, setContractSections } = useContractContext();
    const [hash, setHash] = useState(window.location.hash);
    const translationsMap = useTranslationsMap();
    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        setContractSections(
            arrayMove(contract.contractSections, result.source.index, result.destination.index)
        );
    };
    const location = useLocation();
    useEnhancedEffect(() => {
        if (window.location.hash !== hash) {
            setHash(window.location.hash);
            const element = document.getElementById(location.hash);
            element?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [location]);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className="contractClausesBox" style={{ borderBottomWidth: '1px', flexGrow: 1 }}>
                {showLabel && (
                    <div style={{ display: 'flex', alignItems: 'center', padding: '6px' }}>
                        Select each item from the left menu to add it to the document. In the
                        document drag and drop items to change their order and click the arrow on
                        the right of the item to reveal the content
                    </div>
                )}
                <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={getSectionListStyle(snapshot.isDraggingOver)}
                        >
                            {contract.contractSections
                                .filter((c) => c.active)
                                .map((contractSection, index) => {
                                    const { sectionIndexId, segments } = contractSection;
                                    const section = sectionMap.get(sectionIndexId);
                                    if (!section && loading) {
                                        return (
                                            <div key={sectionIndexId}>
                                                <div className="lds-ellipsis">
                                                    <div></div>
                                                    <div></div>
                                                    <div></div>
                                                    <div></div>
                                                </div>
                                            </div>
                                        );
                                    }
                                    if (!section) {
                                        return null;
                                    }
                                    const translation = translated
                                        ? translationsMap.get(`${section.id}:name`)
                                        : undefined;
                                    const selected = selection === contractSection.sectionIndexId;
                                    return (
                                        <Draggable
                                            key={section.indexId()}
                                            draggableId={sectionIndexId}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    style={getSectionStyle(snapshot.isDragging, {
                                                        ...provided.draggableProps.style,
                                                        paddingTop: '6px',
                                                        paddingBottom: '6px',
                                                    })}
                                                >
                                                    <div
                                                        className="segmentItem"
                                                        style={{
                                                            ...itemStyles(1, '#D8D8D8'),
                                                            border: selected
                                                                ? '3px solid #18a0fb'
                                                                : '1px solid #D8D8D8',
                                                        }}
                                                        {...provided.dragHandleProps}
                                                        onClick={() =>
                                                            setSelection(
                                                                contractSection.sectionIndexId
                                                            )
                                                        }
                                                        id={`#${section.__type}-${section.id}`}
                                                    >
                                                        <DragIndicatorIcon
                                                            htmlColor="#818181"
                                                            fontSize="small"
                                                            style={{ marginRight: '10px' }}
                                                            onClick={() => null}
                                                        />
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                width: '100%',
                                                                color:
                                                                    translated && !translation
                                                                        ? 'red'
                                                                        : 'black',
                                                            }}
                                                        >
                                                            <div>
                                                                {translation
                                                                    ? translation.content
                                                                    : section.name}
                                                            </div>
                                                            <div>
                                                                {section.coverage
                                                                    ? 'Coverage section'
                                                                    : ''}
                                                            </div>
                                                        </div>
                                                        <div className="segmentItemDelete">
                                                            <div style={{ width: '16px' }}></div>
                                                            <DeleteOutlineIcon
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setContractSections(
                                                                        contract.contractSections.map(
                                                                            (contractSection_) =>
                                                                                contractSection_.sectionIndexId ===
                                                                                contractSection.sectionIndexId
                                                                                    ? {
                                                                                          ...contractSection_,
                                                                                          active: false,
                                                                                      }
                                                                                    : contractSection_
                                                                        )
                                                                    );
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <Segments
                                                        contractSection={contractSection}
                                                        segments={segments}
                                                        translated={translated}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </div>
        </DragDropContext>
    );
};
